/**
 * API functions for Title Folder Audit management
 */

import { apiRequest } from "./api";

// ============================================================================
// Types
// ============================================================================

export type LocationState = 'InSharePoint' | 'Archived' | 'Missing' | 'Restoring';
export type AuditStatus = 'Valid' | 'NamingMismatch' | 'WrongBucket' | 'Orphan';
export type FindingType = 'missing_folder' | 'orphan_folder' | 'wrong_location' | 'naming_mismatch';
export type FindingSeverity = 'high' | 'medium' | 'low';
export type ResolutionStatus = 'Unresolved' | 'Linked' | 'Deleted' | 'Ignored';

export interface TitleFolderIndexRecord {
  titleId: string;
  projectNumber: string | null;
  titleName: string | null;
  authorName: string | null;
  kupFolder: string | null;
  titleStatus: string | null;
  expectedFolderName: string | null;
  expectedBucket: string | null;
  locationState: LocationState;
  currentFolderPath: string | null;
  currentBucket: string | null;
  currentSiteId: string | null;
  currentDriveId: string | null;
  currentFolderId: string | null;
  currentWebUrl: string | null;
  currentArchiveStorageAccountId: string | null;
  currentArchiveContainerName: string | null;
  currentArchiveBlobPrefix: string | null;
  currentArchiveUrl: string | null;
  originalFolderPath: string | null;
  originalBucket: string | null;
  fileCount: number | null;
  folderCount: number | null;
  totalSizeBytes: number | null;
  auditStatus: AuditStatus | null;
  lastAuditJobId: string | null;
  lastAuditedUtc: string | null;
  lastArchiveUtc: string | null;
  lastRestoreUtc: string | null;
  archiveCount: number;
  restoreCount: number;
  createdUtc: string;
  modifiedUtc: string;
  errorMessage: string | null;
}

export interface OrphanFolderRecord {
  id: number;
  folderPath: string;
  auditJobId: string | null;
  folderName: string;
  bucketFolder: string | null;
  siteId: string | null;
  driveId: string | null;
  folderId: string | null;
  webUrl: string | null;
  parsedProjectNumber: string | null;
  parsedAuthorName: string | null;
  fileCount: number | null;
  folderCount: number | null;
  totalSizeBytes: number | null;
  discoveredAtUtc: string;
  lastSeenAtUtc: string;
  resolutionStatus: ResolutionStatus | null;
  resolvedToTitleId: string | null;
  resolvedUtc: string | null;
  resolvedBy: string | null;
  resolutionNotes: string | null;
}

export interface AuditFinding {
  findingType: FindingType;
  severity: FindingSeverity;
  titleId?: string;
  projectNumber?: string;
  titleName?: string;
  expectedFolderName?: string;
  actualFolderPath?: string;
  expectedBucket?: string;
  actualBucket?: string;
  titleStatus?: string;
  recommendation: string;
}

export interface AuditSummary {
  auditJobId: string;
  executedAtUtc: string;
  durationMs: number;
  totalTitlesInDataverse: number;
  totalFoldersInSharePoint: number;
  totalBucketsScanned: number;
  matchedCorrectly: number;
  missingFolders: number;
  orphanFolders: number;
  wrongLocation: number;
  namingMismatches: number;
  errors: number;
  siteId: string;
  driveId: string;
  titleStatusFilter: string[] | null;
}

export interface AuditRunResult {
  success: boolean;
  summary: AuditSummary;
  findingsCount: number;
  findings: AuditFinding[];
  message?: string;
}

export interface TitleFolderAuditConfig {
  siteId: string;
  driveId: string;
  rootFolderPath?: string;
  titleStatusFilter?: string[];
  dryRun?: boolean;
}

export interface PaginatedResponse<T> {
  rows: T[];
  totalCount: number;
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Run a title folder audit
 */
export async function runTitleFolderAudit(config: TitleFolderAuditConfig): Promise<AuditRunResult> {
  const response = await apiRequest('POST', '/api/title-folder-audit/run', config);
  return response.json();
}

/**
 * Get audit summary for a specific job
 */
export async function getAuditSummary(auditJobId: string): Promise<AuditSummary | null> {
  try {
    const response = await apiRequest('GET', `/api/title-folder-audit/summary/${auditJobId}`);
    if (response.status === 404) return null;
    return response.json();
  } catch (error: any) {
    if (error.status === 404) return null;
    throw error;
  }
}

/**
 * Search title folders
 */
export async function searchTitleFolders(params: {
  titleId?: string;
  projectNumber?: string;
  locationState?: LocationState;
  auditStatus?: AuditStatus;
  bucket?: string;
  limit?: number;
  offset?: number;
}): Promise<PaginatedResponse<TitleFolderIndexRecord>> {
  const searchParams = new URLSearchParams();
  if (params.titleId) searchParams.set('titleId', params.titleId);
  if (params.projectNumber) searchParams.set('projectNumber', params.projectNumber);
  if (params.locationState) searchParams.set('locationState', params.locationState);
  if (params.auditStatus) searchParams.set('auditStatus', params.auditStatus);
  if (params.bucket) searchParams.set('bucket', params.bucket);
  if (params.limit) searchParams.set('limit', params.limit.toString());
  if (params.offset) searchParams.set('offset', params.offset.toString());

  const response = await apiRequest('GET', `/api/title-folder-audit/folders?${searchParams.toString()}`);
  return response.json();
}

/**
 * Get orphan folders
 */
export async function getOrphanFolders(params: {
  search?: string;
  auditJobId?: string;
  bucketFolder?: string;
  resolutionStatus?: ResolutionStatus;
  limit?: number;
  offset?: number;
}): Promise<PaginatedResponse<OrphanFolderRecord>> {
  const searchParams = new URLSearchParams();
  if (params.search?.trim()) searchParams.set('search', params.search.trim());
  if (params.auditJobId) searchParams.set('auditJobId', params.auditJobId);
  if (params.bucketFolder) searchParams.set('bucketFolder', params.bucketFolder);
  if (params.resolutionStatus) searchParams.set('resolutionStatus', params.resolutionStatus);
  if (params.limit) searchParams.set('limit', params.limit.toString());
  if (params.offset) searchParams.set('offset', params.offset.toString());

  const response = await apiRequest('GET', `/api/title-folder-audit/orphans?${searchParams.toString()}`);
  return response.json();
}

/**
 * Resolve an orphan folder by linking it to a title
 */
export async function resolveOrphanFolder(
  orphanId: number,
  titleId: string,
  resolvedBy: string,
  notes?: string
): Promise<{ success: boolean; message: string }> {
  const response = await apiRequest('POST', `/api/title-folder-audit/orphans/${orphanId}/resolve`, { titleId, resolvedBy, notes });
  return response.json();
}

/**
 * Get dashboard statistics (aggregates from recent audits)
 */
export async function getTitleFolderDashboardStats(): Promise<{
  totalIndexed: number;
  byLocationState: Record<LocationState, number>;
  byAuditStatus: Record<AuditStatus, number>;
  orphanCount: number;
  lastAuditJobId: string | null;
  lastAuditedUtc: string | null;
}> {
  // Fetch recent folder data and aggregate
  const [folders, orphans] = await Promise.all([
    searchTitleFolders({ limit: 1 }),
    getOrphanFolders({ limit: 1 }),
  ]);

  // Get counts by state
  const inSharePoint = await searchTitleFolders({ locationState: 'InSharePoint', limit: 1 });
  const archived = await searchTitleFolders({ locationState: 'Archived', limit: 1 });
  const missing = await searchTitleFolders({ locationState: 'Missing', limit: 1 });
  const restoring = await searchTitleFolders({ locationState: 'Restoring', limit: 1 });

  // Get counts by audit status
  const valid = await searchTitleFolders({ auditStatus: 'Valid', limit: 1 });
  const namingMismatch = await searchTitleFolders({ auditStatus: 'NamingMismatch', limit: 1 });
  const wrongBucket = await searchTitleFolders({ auditStatus: 'WrongBucket', limit: 1 });

  return {
    totalIndexed: folders.totalCount,
    byLocationState: {
      InSharePoint: inSharePoint.totalCount,
      Archived: archived.totalCount,
      Missing: missing.totalCount,
      Restoring: restoring.totalCount,
    },
    byAuditStatus: {
      Valid: valid.totalCount,
      NamingMismatch: namingMismatch.totalCount,
      WrongBucket: wrongBucket.totalCount,
      Orphan: orphans.totalCount,
    },
    orphanCount: orphans.totalCount,
    lastAuditJobId: folders.rows[0]?.lastAuditJobId ?? null,
    lastAuditedUtc: folders.rows[0]?.lastAuditedUtc ?? null,
  };
}

// Helper to format bytes
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return '-';
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

// Helper to format relative time
export function formatRelativeTime(dateString: string | null | undefined): string {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}
